<template>
  <div v-if="address && mapUrl" class="mt-4">
    <div class="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Location Map</div>
    <div 
      class="rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 cursor-pointer shadow-sm hover:shadow-md transition-shadow relative"
      @click="showModal = true"
    >
      <img :src="mapUrl" alt="Event Location" class="w-full h-32 object-cover" />
      <div class="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-10 transition-colors flex items-center justify-center">
        <svg class="w-8 h-8 text-white opacity-0 hover:opacity-100 drop-shadow-md" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
        </svg>
      </div>
    </div>

    <!-- Full Size Map Modal -->
    <div v-if="showModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-75" @click.self="showModal = false">
      <div class="relative w-full max-w-4xl bg-white dark:bg-gray-800 rounded-lg shadow-xl overflow-hidden flex flex-col max-h-[90vh]">
        <div class="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-white dark:bg-gray-800">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white truncate pr-4">{{ address }}</h3>
          <button @click="showModal = false" class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 focus:outline-none">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div class="flex-1 overflow-hidden bg-gray-100 dark:bg-gray-900">
          <img :src="fullMapUrl" alt="Event Location Full" class="w-full h-full object-contain" />
        </div>
        <div class="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex justify-end">
            <a 
                :href="googleMapsUrl" 
                target="_blank" 
                class="inline-flex items-center px-4 py-2 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 border border-indigo-600 dark:border-indigo-400 rounded-md hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
            >
                Open in Google Maps
            </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, defineProps, computed } from 'vue';

const props = defineProps({
  address: {
    type: String,
    required: true
  }
});

const mapUrl = ref(null);
const fullMapUrl = ref(null);
const showModal = ref(false);
const coordinates = ref(null);

const googleMapsUrl = computed(() => {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(props.address)}`;
});

const MAPBOX_TOKEN = window.__RUNTIME_CONFIG__?.VUE_APP_MAPBOX_TOKEN || process.env.VUE_APP_MAPBOX_TOKEN;

const fetchCoordinates = async () => {
  if (!props.address || !MAPBOX_TOKEN) return;
  
  try {
    const response = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(props.address)}.json?access_token=${MAPBOX_TOKEN}&limit=1`);
    const data = await response.json();
    if (data && data.features && data.features.length > 0) {
      const [lng, lat] = data.features[0].center;
      coordinates.value = { lng, lat };
      
      // Small thumbnail url (400x150)
      mapUrl.value = `https://api.mapbox.com/styles/v1/mapbox/streets-v11/static/pin-s+f84e4e(${lng},${lat})/${lng},${lat},14/600x150@2x?access_token=${MAPBOX_TOKEN}`;
      
      // Full size url (800x600)
      fullMapUrl.value = `https://api.mapbox.com/styles/v1/mapbox/streets-v11/static/pin-l+f84e4e(${lng},${lat})/${lng},${lat},15/800x600@2x?access_token=${MAPBOX_TOKEN}`;
    }
  } catch (error) {
    console.error('Error geocoding address for map', error);
  }
};

watch(() => props.address, () => {
  fetchCoordinates();
});

onMounted(() => {
  fetchCoordinates();
});
</script>
